



















import Vue from 'vue';
import {
  Component, Emit, Watch, Provide,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Mutation } from 'vuex-class';
import {
  KPortalHeader,
  KAppToolbar,
  KNoticeContainer,
  KNavigationDrawer,
} from '@kasasa/fbase-components';
import {
  MenuItem,
  Alert,
  NoticeClass,
  AuthStore,
} from '@kasasa/fbase-components/lib';
import { I18nResource, StringMap } from '@/api/i18n';
import { GraphResource, Graph } from '@/api/graph';
import { SettingsResource, Settings } from '@/api/settings';
import { DocumentResource } from '@/api/document';
import { MacroResource } from '@/api/macro';
import { TestResource } from '@/api/test';
import LastUpdatedDateResource from '@/api/lastupdateddate/LastUpdatedDateResource';

@Component({
  components: {
    KPortalHeader,
    KNavigationDrawer,
    KNoticeContainer,
    KAppToolbar,
  },
  computed: mapState(['auth', 'fiId', 'fiSettings']),
})
export default class App extends Vue {
  appTitle: string = 'RegGen';

  fiId!: number;

  fiSettings!: Settings;

  clientId!: number | null;

  auth!: AuthStore;

  menuDefinitions: MenuItem[] = [];

  loading: number = 0;

  graphMap: Map<string, Graph> = new Map<string, Graph>();

  @Mutation('fiId') mutationFi: any;

  @Mutation('isFiUser') mutationIsFiUser: any;

  @Mutation('isAdminUser') mutationIsAdmin: any;

  @Mutation('stringMap') mutationStringMap: any;

  @Mutation('graphMap') mutationGraphMap: any;

  @Mutation('fiSettings') mutationFiSettings: any;

  @Mutation('lastUpdatedDate') mutationLastUpdatedDate: any;

  @Provide('SettingsResource')
  private settingsResource: SettingsResource =
    new SettingsResource(this.$store);

  @Provide('DocumentResource')
  private documentResource: DocumentResource =
    new DocumentResource(this.$store);

  @Provide('MacroResource')
  private macroResource: MacroResource =
    new MacroResource(this.$store);

  @Provide('TestResource')
  private testResource: TestResource =
    new TestResource(this.$store);

  private graphResource: GraphResource =
    new GraphResource(this.$store);

  isAdmin: boolean = false;

  drawer: boolean = true;

  fiSettingsValidated = false;

  mounted() {
    this.loadStrings();
    this.loadGraphs();
    this.loadLastUpdatedDate();
    this.onAuth();
    this.onNewRoute();
  }

  loadStrings() {
    this.loading++;
    const i18nResource: I18nResource = new I18nResource(this.$store);
    i18nResource.get().then((strings:StringMap) => {
      this.mutationStringMap(strings);
    }).catch((error:any) => {
      if (error.response.status !== 401) {
        this.showErrorMessage(error);
      }
    }).finally(() => {
      this.loading--;
    });
  }

  async loadGraphs() {
    this.loading++;
    await this.graphResource.get().then((graphs:Graph[]) => {
      graphs.forEach((graph: Graph) => {
        this.graphMap.set(graph.id, graph);
      });

      this.mutationGraphMap(this.graphMap);
    }).catch((error:any) => {
      if (error.response.status !== 401) {
        this.showErrorMessage(error);
      }
    }).finally(() => {
      this.loading--;
    });
  }

  loadFiSettings(fiId: number) {
    this.loading++;
    this.settingsResource.get(fiId).then((settings:Settings) => {
      this.mutationFiSettings(settings);
      this.validateSettings(settings);
    }).catch((error:any) => {
      if (error.response.status !== 401) {
        this.showErrorMessage(error);
      }
    }).finally(() => {
      this.loading--;
    });
  }

  async validateSettings(settings:Settings | null) {
    // Should have loaded by now, but just in case.
    if (this.graphMap.size === 0) {
      await this.loadGraphs();
    }

    const setupGraph:Graph | undefined = this.graphMap.get('settings');

    if (!setupGraph) {
      this.showErrorMessage('Unable to find setup question graph!');
    } else if (!settings || !settings.attributes || !setupGraph.isTerminalPath(settings.attributes)) {
      // Not fully set up, so force a trip to the setup page.
      if (!this.$route.path.includes('/setup')) {
        this.$router.push({ name: 'setupMain', params: { clientId: this.clientId!.toString(10) } });
      }
    } else {
      this.fiSettingsValidated = true;
    }
  }

  loadLastUpdatedDate() {
    this.loading++;
    const lastUpdatedResource: LastUpdatedDateResource = new LastUpdatedDateResource(this.$store);
    lastUpdatedResource.get().then((lastUpdated:string) => {
      this.mutationLastUpdatedDate(lastUpdated);
    }).catch((error:any) => {
      if (error.response.status !== 401) {
        this.showErrorMessage(error);
      }
    }).finally(() => {
      this.loading--;
    });
  }

  @Watch('auth', { deep: true })
  onAuth() {
    if (this.auth.sessionData && this.auth.sessionData.fkAuthLevel === 'client') {
      this.mutationIsFiUser(true);
      this.drawer = false;
    } else if (this.auth.headerData) {
      this.mutationIsFiUser(false);
      this.isAdmin = this.auth!.authManager!.canWrite('complianceAdmin');
      this.mutationIsAdmin(this.isAdmin);
    }

    this.buildMenu(null);
  }

  @Watch('auth', { deep: true })
  @Watch('$route', { deep: true })
  onNewRoute() {
    let newFiId: number | null = null;
    let clientId: number | null = null;
    if (this.auth.sessionData && this.auth.sessionData.fkAuthLevel === 'client') {
      // This is an fi-level user, so set the fi from their session and ignore anything from the url
      newFiId = this.auth.sessionData.crmId; // Yes, this is confusing.  crmId is what we traditionall call fiId.  fiId is client id now.
      clientId = this.auth.sessionData.fiId;
    } else if (this.auth.headerData) {
      // Header data may not have been loaded yet, so hold off until it is so we can do clientId=>fiId lookup
      const clientIdString:string|undefined = this.$route.params.clientId;
      if (clientIdString) {
        clientId = Number.parseInt(clientIdString, 10);
        newFiId = this.fiIdFromClientId(Number(clientId));
      }
    }

    if (newFiId !== null && newFiId !== this.fiId) {
      this.fiSettingsValidated = false;
      this.mutationFi(newFiId);
      this.mutationFiSettings(null);
      this.clientId = clientId;
      this.buildMenu(clientId!.toString());
      this.loadFiSettings(newFiId);
    } else if (!this.fiSettingsValidated && this.clientId) {
      this.validateSettings(this.fiSettings);
    }
  }

  fiIdFromClientId(clientId: number): number | null {
    let fiId: number | null = null;
    if (this.auth && this.auth.headerData) {
      for (let i: number = 0; i < this.auth.headerData.fis.length; i++) {
        // The linter insists on '===' here but that doesn't find a match. Not sure why.
        // Even defining fiId as number|null, the same as in the fi class, doesn't help.
        /* eslint-disable eqeqeq */
        if (this.auth.headerData.fis[i].id == clientId) {
          /* eslint-enable eqeqeq */
          fiId = this.auth.headerData.fis[i].fiId;
          break;
        }
      }
    }

    return fiId;
  }

  buildMenu(clientId: string | null) {
    this.menuDefinitions = [];

    if (clientId) {
      this.menuDefinitions.push({ label: 'Account Disclosures', to: { name: 'adgMain', params: { clientId } } });
      this.menuDefinitions.push({ label: 'Marketing Disclaimers', to: { name: 'mdgMain', params: { clientId } } });
      this.menuDefinitions.push({ label: 'Cycle Dates', to: { name: 'cycleDates', params: { clientId } } });
      this.menuDefinitions.push({ label: 'FI Setup', to: { name: 'setupMain', params: { clientId } } });
      this.menuDefinitions.push({ label: 'Usage Report', to: { name: 'usageReport', params: { clientId } } });
    } else {
      this.menuDefinitions.push({ label: 'Account Disclosures', to: { name: 'adgLanding' } });
      this.menuDefinitions.push({ label: 'Marketing Disclaimers', to: { name: 'mdgLanding' } });
      this.menuDefinitions.push({ label: 'Cycle Dates', to: { name: 'cycleDatesLanding' } });
      this.menuDefinitions.push({ label: 'FI Setup', to: { name: 'setupLanding' } });
      this.menuDefinitions.push({ label: 'Usage Report', to: { name: 'usageReport' } });
    }

    if (this.isAdmin) {
      this.menuDefinitions.push({ label: 'Macro Editor', to: { name: 'macros' } });
      this.menuDefinitions.push({ label: 'Testing Tool', to: { name: 'tests' } });
    }
  }

  @Emit('error')
  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
